/**
 * Keyboard Shortcuts Guide Component
 * 
 * Shows keyboard shortcuts when user presses '?'
 * 
 * Version: 6.2.0
 */
import React, { useState, useEffect, useCallback } from 'react'
import './KeyboardShortcuts.css'

interface ShortcutGroup {
  title: string
  shortcuts: {
    key: string
    description: string
    action?: string
  }[]
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { key: 'G D', description: 'Go to Dashboard' },
      { key: 'G S', description: 'Go to Strategies' },
      { key: 'G P', description: 'Go to Paper Trading' },
      { key: 'G A', description: 'Go to Analytics' },
    ]
  },
  {
    title: 'Actions',
    shortcuts: [
      { key: 'N', description: 'New Strategy' },
      { key: 'E', description: 'Edit Selected' },
      { key: 'D', description: 'Duplicate' },
      { key: '⌫', description: 'Delete Selected', action: 'danger' },
    ]
  },
  {
    title: 'Trading',
    shortcuts: [
      { key: 'Space', description: 'Quick Trade' },
      { key: 'S', description: 'Stop All Bots' },
      { key: 'R', description: 'Refresh Data' },
      { key: 'P', description: 'Pause/Resume' },
    ]
  },
  {
    title: 'General',
    shortcuts: [
      { key: '?', description: 'Show Shortcuts' },
      { key: 'Esc', description: 'Close / Cancel' },
      { key: '/', description: 'Search' },
      { key: '⌘ K', description: 'Command Palette' },
    ]
  }
]

interface KeyboardShortcutsProps {
  isOpen: boolean
  onClose: () => void
}

const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null

  return (
    <div className="shortcuts-overlay" onClick={onClose}>
      <div className="shortcuts-modal" onClick={(e) => e.stopPropagation()}>
        <div className="shortcuts-header">
          <div className="shortcuts-title">
            <span className="shortcuts-icon">⌨️</span>
            <h2>Keyboard Shortcuts</h2>
          </div>
          <button className="shortcuts-close" onClick={onClose}>✕</button>
        </div>

        <div className="shortcuts-content">
          <div className="shortcuts-intro">
            <p>Press <kbd>?</kbd> anytime to toggle this guide</p>
          </div>

          <div className="shortcuts-grid">
            {SHORTCUT_GROUPS.map((group) => (
              <div key={group.title} className="shortcut-group">
                <h3 className="group-title">{group.title}</h3>
                <div className="shortcut-list">
                  {group.shortcuts.map((shortcut) => (
                    <div key={shortcut.key} className="shortcut-item">
                      <kbd className={`shortcut-key ${shortcut.action || ''}`}>
                        {shortcut.key}
                      </kbd>
                      <span className="shortcut-desc">{shortcut.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="shortcuts-footer">
            <p>💡 Pro tip: Use <kbd>G</kbd> then <kbd>D</kbd> to quickly navigate (vim-style)</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Hook to manage keyboard shortcuts
export function useKeyboardShortcuts(shortcuts: Record<string, () => void>) {
  const [showGuide, setShowGuide] = useState(false)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Ignore if typing in input/textarea
    const target = e.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return
    }

    // Check for shortcuts
    const key = e.key.toLowerCase()

    // Show shortcuts guide
    if (key === '?' || (e.shiftKey && key === '/')) {
      e.preventDefault()
      setShowGuide(prev => !prev)
      return
    }

    // Close on Escape
    if (key === 'escape') {
      setShowGuide(false)
      return
    }

    // Execute registered shortcuts
    const shortcutKey = e.shiftKey ? `shift+${key}` : key
    if (shortcuts[shortcutKey]) {
      e.preventDefault()
      shortcuts[shortcutKey]()
    }
  }, [shortcuts])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return {
    showGuide,
    setShowGuide,
    KeyboardShortcutsComponent: (
      <KeyboardShortcuts 
        isOpen={showGuide} 
        onClose={() => setShowGuide(false)} 
      />
    )
  }
}

export default KeyboardShortcuts